import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import {
  InstantSearch,
  Configure,
  useStats,
  useRefinementList,
} from "react-instantsearch-hooks";
import { useSelector } from "react-redux";
import config from "@/config";
import MasterMenuHits from "./MasterMenuHits.jsx";
import MasterMenuPagination from "./MasterMenuPagination.jsx";
import MasterMenuUnmatchedUPCs from "./MasterMenuUnmatchedUPCs.jsx";
import MasterMenuDraftProducts from "./MasterMenuDraftProducts.jsx";
import Autocomplete from "./autocomplete.jsx";
import Loader from "@/components/Loader.jsx";
import { PrimaryButton } from "@/components/Buttons.jsx";
import DocumentModal from "@/components/DocumentModal.jsx";
import ProductImageModal from "@/components/ProductImageModal.jsx";

const tabBtnClass = (active) =>
  `inline-flex items-center justify-center text-xs font-medium leading-5 rounded-full px-3 py-1 border shadow-sm duration-150 ease-in-out ${
    active
      ? "border-theme-button-primary bg-theme-button-primary text-theme-button-primary-text"
      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
  }`;

function MasterMenuUpcFilterTabs({ value, onChange }) {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
        UPC Filters
      </span>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className={tabBtnClass(value === "all")}
          onClick={() => onChange("all")}
        >
          All Products
        </button>
        <button
          type="button"
          className={tabBtnClass(value === "no_upc")}
          onClick={() => onChange("no_upc")}
        >
          Product without UPC
        </button>
        <button
          type="button"
          className={tabBtnClass(value === "conflict")}
          onClick={() => onChange("conflict")}
        >
          Conflicting UPC
        </button>
        <button
          type="button"
          className={tabBtnClass(value === "unmatched")}
          onClick={() => onChange("unmatched")}
        >
          Unmatched UPC
        </button>
        <button
          type="button"
          className={tabBtnClass(value === "drafts")}
          onClick={() => onChange("drafts")}
        >
          Draft Products
        </button>
      </div>
    </div>
  );
}

const CustomStates = React.memo(() => {
  const { nbHits } = useStats();
  return <>{nbHits}</>;
});

const CustomRefinementList = React.memo(() => {
  const {
    items: categoryItems,
    refine: categoryRefine,
    toggleShowMore: categoryToggleShowMore,
    hasExhaustiveItems: categoryCanToggleShowMore,
    isShowingMore: categoryIsShowingMore,
  } = useRefinementList({
    attribute: "default_category",
    sortBy: ["count"],
    limit: 10,
    showMoreLimit: 500,
    showMore: true,
  });

  const {
    items: brandItems,
    refine: brandRefine,
    toggleShowMore: brandToggleShowMore,
    hasExhaustiveItems: brandCanToggleShowMore,
    isShowingMore: brandIsShowingMore,
    searchForItems,
  } = useRefinementList({
    attribute: "company",
    sortBy: ["count"],
    limit: 10,
    showMoreLimit: 1000,
    showMore: true,
  });

  const {
    items: tagItems,
    refine: tagRefine,
    toggleShowMore: tagToggleShowMore,
    hasExhaustiveItems: tagCanToggleShowMore,
    isShowingMore: tagIsShowingMore,
    searchForItems: searchForTags,
  } = useRefinementList({
    attribute: "attribute_tags",
    sortBy: ["count"],
    limit: 10,
    showMoreLimit: 500,
    showMore: true,
  });

  const brandSearchBox = useRef(null);
  const tagSearchBox = useRef(null);

  return (
    <div className="col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
      <div className="flex flex-col h-full p-5">
        <header>
          <div className="flex items-center justify-between font-bold">
            Categories
          </div>
        </header>
        <div className="grow mt-2">
          {categoryItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between py-2"
            >
              <input
                type="checkbox"
                id={`category-${item.label}`}
                name={item.label}
                value={item.label}
                checked={item.isRefined}
                onChange={() => categoryRefine(item.value)}
                className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
              />
              <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                {item.label}
              </span>
              <div className="text-sm font-medium text-slate-500">
                {item.count}
              </div>
            </div>
          ))}
          {(!categoryCanToggleShowMore || categoryIsShowingMore) && (
            <button
              type="button"
              onClick={categoryToggleShowMore}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:hover:text-white"
            >
              {categoryIsShowingMore ? "Show Less" : "Show More"}
            </button>
          )}
        </div>

        <header>
          <div className="flex items-center justify-between mt-8 font-bold">
            Brands
          </div>
        </header>
        <div className="grow mt-2">
          <input
            className="form-input w-full"
            type="search"
            placeholder={"Search Brands..."}
            ref={brandSearchBox}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={512}
            onChange={(event) => searchForItems(event.currentTarget.value)}
          />
          {brandItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between py-2"
            >
              <input
                type="checkbox"
                id={`brand-${item.label}`}
                name={item.label}
                value={item.label}
                checked={item.isRefined}
                onChange={() => brandRefine(item.value)}
                className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
              />
              <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                {item.label}
              </span>
              <div className="text-sm font-medium text-slate-500">
                {item.count}
              </div>
            </div>
          ))}
          {(!brandCanToggleShowMore || brandIsShowingMore) &&
            !brandSearchBox.current?.value && (
              <button
                type="button"
                onClick={brandToggleShowMore}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:hover:text-white"
              >
                {brandIsShowingMore ? "Show Less" : "Show More"}
              </button>
            )}
        </div>

        <header>
          <div className="flex items-center justify-between mt-8 font-bold">
            Attribute Tags
          </div>
        </header>
        <div className="grow mt-2">
          <input
            className="form-input w-full"
            type="search"
            placeholder="Search Tags..."
            ref={tagSearchBox}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={512}
            onChange={(event) => searchForTags(event.currentTarget.value)}
          />
          {tagItems.length === 0 ? (
            <p className="text-sm text-slate-400 mt-2">No attribute tags found.</p>
          ) : (
            tagItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between py-2"
              >
                <input
                  type="checkbox"
                  id={`attribute-tag-${item.label}`}
                  name={item.label}
                  value={item.label}
                  checked={item.isRefined}
                  onChange={() => tagRefine(item.value)}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                />
                <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                  {item.label}
                </span>
                <div className="text-sm font-medium text-slate-500">
                  {item.count}
                </div>
              </div>
            ))
          )}
          {(!tagCanToggleShowMore || tagIsShowingMore) &&
            !tagSearchBox.current?.value &&
            tagItems.length > 0 && (
              <button
                type="button"
                onClick={tagToggleShowMore}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:hover:text-white"
              >
                {tagIsShowingMore ? "Show Less" : "Show More"}
              </button>
            )}
        </div>
      </div>
    </div>
  );
});

const MasterMenuManagement = () => {
  const navigate = useNavigate();
  const [searchClient, setSearchClient] = useState(null);
  const [documentModalOpen, setDocumentModalOpen] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(null);
  const [upcFilter, setUpcFilter] = useState("all"); // all | no_upc | conflict | unmatched | drafts

  const productHitsRef = useRef();

  const { selectedLocation } = useSelector((state) => state.app);

  useEffect(() => {
    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.ts.key,
        nodes: config.ts.nodes,
        nearestNode: config.ts.nearestNode,
        cacheSearchResultsForSeconds: 0,
      },
      additionalSearchParameters: {
        query_by: "title,subtitle,description,default_category,company",
        groupBy: "sku_id",
        groupLimit: 99,
      },
    });
    const searchClient = typesenseInstantsearchAdapter.searchClient;
    setSearchClient(searchClient);
  }, []);

  const { indexName, filter, viewName, missingUpcMode, tableHeaders } = useMemo(() => {
    if (!selectedLocation) {
      return {
        filter: "",
        indexName: "",
        viewName: "All Verified Products",
        missingUpcMode: false,
        tableHeaders: ["Product", "Brand", "Saucey Category"],
      };
    }
    productHitsRef.current?.bulkEdit("clear-selected");
    const parts = [];
    let indexName = "";
    let viewName = "All Verified Products";
    const missingUpcMode = upcFilter === "no_upc";

    indexName = config.ts.index;

    if (selectedLocation.tobacco_enabled && selectedLocation.alcohol_enabled) {
      if (!["nmi", "frame"].includes(selectedLocation.payment_gateway)) {
        parts.push("is_high_risk: false");
      }
    } else if (
      selectedLocation.tobacco_enabled &&
      !selectedLocation.alcohol_enabled
    ) {
      if (!["nmi", "frame"].includes(selectedLocation.payment_gateway)) {
        parts.push("(is_tobacco: true && is_high_risk: false)");
      } else {
        parts.push("(is_tobacco: true || is_high_risk: true)");
      }
    } else if (
      selectedLocation.alcohol_enabled &&
      !selectedLocation.tobacco_enabled
    ) {
      if (!["nmi", "frame"].includes(selectedLocation.payment_gateway)) {
        parts.push("(is_tobacco:!= true && is_high_risk: false)");
      } else {
        parts.push("(is_tobacco:!= true || is_high_risk: true)");
      }
    } else {
      parts.push("base_category: NOTENABLED");
    }

    if (upcFilter === "no_upc") {
      viewName = "Products missing UPC";
      parts.push("is_upc_empty:true");
      parts.push("recipe:!= true");
    } else if (upcFilter === "conflict") {
      // placeholder for conflicting UPCs
    }

    const tableHeaders = missingUpcMode
      ? ["Product", "Brand", "Saucey Category", "Add UPC"]
      : ["Product", "Brand", "Saucey Category", ""];

    return {
      filter: parts.join(" && "),
      viewName,
      indexName,
      missingUpcMode,
      tableHeaders,
    };
  }, [selectedLocation, upcFilter]);

  if (!selectedLocation) {
    return null;
  }

  if (upcFilter === "unmatched" || upcFilter === "drafts") {
    const panelTitle =
      upcFilter === "drafts" ? "Draft products" : "Unmatched retailer UPCs";
    const PanelBody =
      upcFilter === "drafts" ? MasterMenuDraftProducts : MasterMenuUnmatchedUPCs;

    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 md:py-0 w-full max-w-9xl mx-auto overflow-x-auto">
        <div className="md:py-8 w-full">
          <header className="mb-6">
            <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
              Master Menu Management
            </h1>
          </header>

          <div className="space-y-4 w-full">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
              <div className="min-w-0 flex-1">
                <MasterMenuUpcFilterTabs value={upcFilter} onChange={setUpcFilter} />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end shrink-0 w-full lg:w-auto">
                <PrimaryButton
                  type="button"
                  onClick={() => navigate("/catalog/master-menu/new")}
                >
                  New product
                </PrimaryButton>
              </div>
            </div>

            <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative w-full">
              <header className="px-5 py-4 flex h-16">
                <h2 className="font-semibold text-slate-800 dark:text-slate-100 flex grow items-center">
                  {panelTitle}
                </h2>
              </header>
              <div className="px-5 pb-5">
                <PanelBody />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!searchClient) {
    return <Loader />;
  }

  return (
    <InstantSearch indexName={indexName} searchClient={searchClient}>
      <Configure hitsPerPage={50} filters={filter} />

      <div className="px-4 sm:px-6 lg:px-8 py-8 md:py-0 w-full max-w-9xl mx-auto overflow-x-auto">
        <div className="xl:flex">
          <div className="md:flex flex-1">
            <div className="w-full md:w-80 mb-8 md:mb-0">
              <div className="md:sticky  md:h-[calc(100dvh-64px)] md:overflow-x-hidden md:overflow-y-auto no-scrollbar ">
                <div className="md:py-8">
                  <header className="mb-6">
                    <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                      Master Menu Management
                    </h1>
                  </header>
                  <CustomRefinementList />
                </div>
              </div>
            </div>

            <div className="flex-1 md:ml-8 xl:mx-4 2xl:mx-8 pt-[18px]">
              <div className="md:py-8">
                <div className="space-y-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
                    <div className="min-w-0 flex-1">
                      <MasterMenuUpcFilterTabs value={upcFilter} onChange={setUpcFilter} />
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end shrink-0 w-full lg:w-auto">
                      <div className="w-full sm:max-w-xs md:max-w-sm lg:w-72">
                        <Autocomplete
                          searchClient={searchClient}
                          placeholder="Search products"
                          detachedMediaQuery="none"
                          openOnFocus
                        />
                      </div>
                      <PrimaryButton
                        type="button"
                         onClick={() => navigate("/catalog/master-menu/new")}
                      >
                        New product
                      </PrimaryButton>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
                    <header className="px-5 py-4 flex h-16">
                      <h2 className="font-semibold text-slate-800 dark:text-slate-100 flex grow items-center">
                        {viewName}
                        <span className="text-slate-400 dark:text-slate-500 font-medium ml-2">
                          <CustomStates />
                        </span>
                      </h2>
                    </header>

                    <div>
                      <div className="overflow-x-auto">
                        <MasterMenuHits
                          headers={tableHeaders}
                          missingUpcMode={missingUpcMode}
                        />
                      </div>
                      <MasterMenuPagination resetKey={upcFilter} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DocumentModal
        skuData={documentModalOpen}
        setDocumentModalOpen={setDocumentModalOpen}
        refreshDocumentCounts={(skuIds) =>
          productHitsRef.current?.refreshDocumentCounts(skuIds)
        }
      />

      <ProductImageModal
        skuData={imageModalOpen}
        setImageModalOpen={setImageModalOpen}
        updateImageOverride={(skuId, imageUrl) =>
          productHitsRef.current?.updateImageOverride(skuId, imageUrl)
        }
      />
    </InstantSearch>
  );
};

export default MasterMenuManagement;
